import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Heart, Shield, Sparkles, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

const PausedOverlay = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [unpausing, setUnpausing] = useState(false);
  const [result, setResult] = useState(null);

  const handleUnpause = async () => {
    setUnpausing(true);
    try {
      const data = await apiClient.request('/api/users/subscription/unpause', { method: 'POST' });
      setResult(data);
      if (refreshUser) await refreshUser();
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      console.error(e);
      setResult({ status: 'error', message: 'Something went wrong. Please try again.' });
    }
    setUnpausing(false);
  };

  if (result?.status === 'active') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(26, 18, 9, 0.85)', backdropFilter: 'blur(12px)' }}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center" data-testid="unpause-success">
          <Sparkles size={48} className="mx-auto mb-4" style={{ color: '#F5831F' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1209' }}>Welcome Back!</h2>
          <p className="text-sm mb-2" style={{ color: '#666' }}>{result.message}</p>
          <p className="text-xs" style={{ color: '#999' }}>Reloading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(26, 18, 9, 0.85)', backdropFilter: 'blur(12px)' }} data-testid="paused-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 text-center" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #F5F3FF 100%)' }}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#F5831F15' }}>
            <Heart size={28} style={{ color: '#F5831F' }} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1209' }}>Your Account is Paused</h2>
          <p className="text-sm" style={{ color: '#666' }}>
            Your AvA, conversations, streaks, and all your progress are safely stored and waiting for you.
          </p>
        </div>

        {/* Benefits */}
        <div className="px-8 py-5 space-y-3">
          {[
            { icon: Shield, text: 'All your data is preserved — nothing has been deleted', color: '#10B981' },
            { icon: Sparkles, text: 'New features and updates are ready for you to explore', color: '#7B4FD8' },
            { icon: Heart, text: 'Your AvA remembers your journey and is ready to continue', color: '#F5831F' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <item.icon size={16} className="flex-shrink-0 mt-0.5" style={{ color: item.color }} />
              <p className="text-sm" style={{ color: '#444' }}>{item.text}</p>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 pt-3 space-y-3">
          <button
            onClick={handleUnpause}
            disabled={unpausing}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:shadow-lg disabled:opacity-60"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="unpause-btn">
            {unpausing
              ? <><Loader2 size={18} className="animate-spin" /> Reactivating...</>
              : <><Play size={18} /> Resume My Subscription</>
            }
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="w-full py-2.5 rounded-xl text-sm font-medium transition-colors hover:bg-gray-50"
            style={{ color: '#999' }}
            data-testid="paused-settings-link">
            Go to Settings
          </button>

          {result?.status === 'error' && (
            <p className="text-center text-xs text-red-500 mt-2">{result.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PausedOverlay;
